import { c as _c } from "react/compiler-runtime";
import { IcoCheck16, IcoCopy16 } from '@onefootprint/icons';
import { IconButton, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { format, isToday } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useEventListener } from 'usehooks-ts';
const COPY_FEEDBACK_MS = 1500;
type MessageActionsProps = {
  content: string;
  /** ISO timestamp the message was created (history) or received (live WS). When
   *  present, renders a relative "just now / 2m ago" caption next to Copy. */
  timestamp?: string;
  /** Keep actions visible at rest (used under the latest assistant turn, à la Claude);
   * otherwise they reveal on hover/focus. */
  alwaysVisible?: boolean;
  /** Hover state from the parent wrapper. CSS `:hover` is unreliable in this chat —
   * tab switches and streaming-induced layout reflow can leave `:hover` stuck on
   * stale messages, producing multiple visible action rows at once. Wrappers thread
   * a JS-tracked hover state through this prop via [[useHoverState]]. */
  visible?: boolean;
};

/**
 * Stateful hover detection used by message wrappers in place of CSS `group-hover`.
 *
 * Why: Chrome doesn't reliably clear `:hover` when the tab loses focus, and the
 * agent's streaming reflow can leave stale `:hover` state on messages the cursor
 * no longer covers. The result is multiple action rows visible at once (Slack
 * #ui-bugs repro: hover a message, switch tabs, return — old hover state sticks).
 *
 * Spread `handlers` on the wrapper element and pass `hovered` to MessageActions /
 * UserMessageActions via their `visible` prop. The hook resets `hovered` to false
 * when the document becomes hidden so returning to the tab starts from a clean
 * state.
 */
export const useHoverState = () => {
  const $ = _c(4);
  const [hovered, setHovered] = useState(false);
  const documentRef = useRef(document);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (document.hidden) {
        setHovered(false);
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEventListener("visibilitychange", t0, documentRef);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false)
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hovered) {
    t2 = {
      hovered,
      handlers: t1
    };
    $[2] = hovered;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};

// Show the absolute local time as the visible label (e.g. "12:34 PM"); fall back
// to a dated form for messages from other days ("Mar 5, 12:34 PM"). Full
// timestamp lives on the tooltip for precision.
const formatTimestamp = (iso: string): {
  label: string;
  full: string;
} | null => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const full = format(date, 'PPpp'); // e.g. "Mar 5, 2026 at 12:34:56 PM"
  const label = isToday(date) ? format(date, 'p') : format(date, 'MMM d, p');
  return {
    label,
    full
  };
};

// Action row shared by user and assistant messages. Revealed on hover/focus, or pinned
// visible for the latest turn. Alignment is controlled by the parent (items-end/start).
const MessageActions = t0 => {
  const $ = _c(21);
  const {
    content,
    timestamp,
    alwaysVisible,
    visible
  } = t0;
  const [copied, setCopied] = useState(false);
  let t1;
  if ($[0] !== content) {
    t1 = () => {
      navigator.clipboard.writeText(content);
      setCopied(true);
    };
    $[0] = content;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleCopy = t1;
  let t2;
  let t3;
  if ($[2] !== copied) {
    t2 = () => {
      if (!copied) {
        return;
      }
      const t = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
      return () => clearTimeout(t);
    };
    t3 = [copied];
    $[2] = copied;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== timestamp) {
    t4 = timestamp ? formatTimestamp(timestamp) : null;
    $[5] = timestamp;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const ts = t4;
  const shown = alwaysVisible || visible;
  const t5 = shown ? "opacity-100" : "opacity-0 max-md:opacity-100";
  let t6;
  if ($[7] !== t5) {
    t6 = cx("flex items-center gap-1 transition-opacity focus-within:opacity-100", t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== ts) {
    t7 = ts && <Tooltip content={ts.full} side="bottom"><span className="text-[12px] text-tertiary px-1 tabular-nums select-none">{ts.label}</span></Tooltip>;
    $[9] = ts;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = copied ? "Copied" : "Copy";
  const t9 = copied ? IcoCheck16 : IcoCopy16;
  let t10;
  if ($[11] !== handleCopy || $[12] !== t9) {
    t10 = <IconButton icon={t9} size="tiny" aria-label="Copy message" onClick={handleCopy} />;
    $[11] = handleCopy;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t8) {
    t11 = <Tooltip content={t8} side="bottom">{t10}</Tooltip>;
    $[14] = t10;
    $[15] = t8;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11 || $[18] !== t6 || $[19] !== t7) {
    t12 = <div className={t6}>{t7}{t11}</div>;
    $[17] = t11;
    $[18] = t6;
    $[19] = t7;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
};
export default MessageActions;